'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';
import { RelatedProductsSkeleton } from './RelatedProductsSkeleton';

interface RandomProduct {
  productId: string;
  name: string;
  slug: string;
  price: number;
  images: string[];
  category: string;
}

interface RelatedProductsProps {
  currentProductId: string;
}

function getVisible(width: number) {
  if (width < 640) return 1;
  if (width < 1024) return 2;
  return 3;
}

export function RelatedProducts({ currentProductId }: RelatedProductsProps) {
  const { language } = useLanguage();
  const t = useTranslation(language);

  const [products, setProducts] = useState<RandomProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [slide, setSlide] = useState(0);
  const [visible, setVisible] = useState(3);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch random products
  useEffect(() => {
    async function fetchRandom() {
      try {
        const res = await fetch('/api/products/products-catalog?limit=20&sortBy=featured');
        const data = await res.json();
        const all: RandomProduct[] = data.products ?? [];
        const filtered = all.filter(p => p.productId !== currentProductId);
        for (let i = filtered.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
        }
        setProducts(filtered.slice(0, 10));
      } finally {
        setLoading(false);
      }
    }
    fetchRandom();
  }, [currentProductId]);

  // Track container width to compute visible count
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      const v = getVisible(w);
      setVisible(v);
      // clamp slide so we never show empty space
      setSlide(s => Math.min(s, Math.max(0, products.length - v)));
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [products.length]);

  const maxSlide = Math.max(0, products.length - visible);
  const prev = useCallback(() => setSlide(s => Math.max(0, s - 1)), []);
  const next = useCallback(() => setSlide(s => Math.min(maxSlide, s + 1)), [maxSlide]);

  if (loading) {
    return (
      <section style={{ marginTop: 'calc(var(--space-8) * 1.6)' }}>
        <h2 style={{ fontSize: 22, margin: '0 0 var(--space-4)' }}>
          {t('product.youMayAlsoLike')}
        </h2>
        <div className="flex gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex-1">
              <RelatedProductsSkeleton />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  const cardWidthPct = 100 / visible;
  const translatePct = slide * cardWidthPct;
  const totalPages = maxSlide + 1;

  return (
    <section style={{ marginTop: 'calc(var(--space-8) * 1.6)' }}>
      <h2 style={{ fontSize: 22, margin: '0 0 var(--space-4)' }}>
        {t('product.youMayAlsoLike')}
      </h2>

      <div className="relative" ref={containerRef}>
        {/* Left arrow */}
        {slide > 0 && (
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-bg)', border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-sm)' }}
            aria-label="Previous products"
          >
            <FiChevronLeft className="w-4 h-4" />
          </button>
        )}

        {/* Right arrow */}
        {slide < maxSlide && (
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-bg)', border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-sm)' }}
            aria-label="Next products"
          >
            <FiChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* Track */}
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-300 ease-in-out"
            style={{ transform: `translateX(-${translatePct}%)` }}
          >
            {products.map((product) => (
              <div
                key={product.productId}
                className="flex-shrink-0 px-2"
                style={{ width: `${cardWidthPct}%` }}
              >
                <Link
                  href={`/shop/${product.productId}?slug=${product.slug}`}
                  className="group card elev-sm block h-full"
                  style={{ padding: 0, overflow: 'hidden' }}
                >
                  <div className="relative w-full" style={{ paddingBottom: '70%' }}>
                    <Image
                      src={product.images[0] || '/placeholder-product.jpg'}
                      alt={product.name}
                      fill
                      className="washed object-cover transition-transform duration-500 group-hover:scale-105"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                  </div>
                  <div style={{ padding: 'var(--space-3)' }}>
                    <p className="line-clamp-2" style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>
                      {product.name}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>
                      {(product.price / 100).toFixed(2)} RON
                    </p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-1.5" style={{ marginTop: 'var(--space-4)' }}>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className="h-1.5 rounded-full transition-all duration-200"
                style={{
                  width: i === slide ? 16 : 6,
                  background: i === slide ? 'var(--color-accent)' : 'var(--color-neutral-300)',
                }}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
